import React, { useEffect, useMemo, useState, useRef } from "react";
import headerLogo from "./header-logo.svg";
import "./style.css";
import ptBrIcon from "../../assets/svg/lang/pt-BR.svg";
import usIcon from "../../assets/svg/lang/us.svg";
import deIcon from "../../assets/svg/lang/de.svg";
import esIcon from "../../assets/svg/lang/es.svg";
import scrollToSection from "../scrollToSection";
import MenuToggleIcon from "../../assets/svg/menuToggle.svg";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { getLocalePath } from "../../i18n";
// import smokeBG from "../../assets/images/smoke.png";

function MenuList({ items, onItemClick, direction = "row" }) {
  const ulClassName =
    direction === "row"
      ? "d-flex align-items-center justify-content-between"
      : "d-flex flex-column menu-items-mobile";

  return (
    <ul className={ulClassName}>
      {items.map((item) => (
        <li
          key={item.targetId}
          className="header-menu__item"
          onClick={() => onItemClick(item.targetId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onItemClick(item.targetId);
          }}
        >
          {item.label}
        </li>
      ))}
    </ul>
  );
}

function MobileDrawer({ open, onClose, children }) {
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    // limpa timers
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);

    if (open) {
      setIsMounted(true);

      // 👇 pequeno delay pro usuário "sentir" a animação
      openTimerRef.current = setTimeout(() => {
        setIsOpen(true);
      }, 90); // ajuste: 60~140ms
    } else {
      setIsOpen(false);

      // tempo igual ao transition do CSS
      closeTimerRef.current = setTimeout(() => {
        setIsMounted(false);
      }, 280);
    }

    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [open]);

  // Fechar no ESC
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Travar scroll do body enquanto aberto
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!isMounted) return null;

  return (
    <>
      <div
        className={`drawer-backdrop ${isOpen ? "is-open" : ""}`}
        onClick={onClose}
      />
      <aside
        className={`drawer ${isOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="drawer-close"
          onClick={onClose}
          aria-label="Close menu"
        >
          ✕
        </button>

        {children}
      </aside>
    </>
  );
}

export const Header = ({ className, locale, content }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const LANGUAGES = [
    { code: "pt-BR", label: "Português", icon: ptBrIcon, href: getLocalePath("pt-BR") },
    { code: "en", label: "English", icon: usIcon, href: getLocalePath("en") },
    { code: "de", label: "Deutsch", icon: deIcon, href: getLocalePath("de") },
    { code: "es", label: "Español", icon: esIcon, href: getLocalePath("es") },
  ];
  const MENU_ITEMS = useMemo(
    () => [
      { label: content.menu.about, targetId: "about" },
      { label: content.menu.news, targetId: "news" },
      { label: content.menu.tour, targetId: "tourDates" },
      { label: content.menu.albums, targetId: "albuns" },
      { label: content.menu.media, targetId: "multi-media" },
      { label: content.menu.follow, targetId: "follow" },
    ],
    [content]
  );

  const LEFT_ITEMS = useMemo(() => MENU_ITEMS.slice(0, 3), [MENU_ITEMS]);
  const RIGHT_ITEMS = useMemo(() => MENU_ITEMS.slice(3), [MENU_ITEMS]);

  function handleGoTo(id) {
    setIsMobileOpen(false); // fecha no mobile
    scrollToSection(id);
    console.log(id);
  }

  return (
    <header
      id="header"
      className={`header ${className ?? ""} d-flex align-items-center`}
    >
      {/* top bar (desktop) */}
      {/* <div className="top-bar d-lg-block d-none">
        <div className="container">
          <ul className="flags-lang d-flex align-items-center justify-content-end">
            <li>
              <a href="/">
                <img src={ptBrIcon} title="pt-BR" width="25px" alt="pt-BR" />
              </a>
            </li>
            <li>
              <a href="/">
                <img src={usIcon} title="us" width="25px" alt="us" />
              </a>
            </li>
            <li>
              <a href="/">
                <img src={deIcon} title="de" width="25px" alt="de" />
              </a>
            </li>
            <li>
              <a href="/">
                <img src={esIcon} title="es" width="25px" alt="es" />
              </a>
            </li>
          </ul>
        </div>
      </div> */}
      <div className="top-bar d-lg-block d-none">
        <div className="container">
          <LanguageSwitcher
            variant="desktop"
            languages={LANGUAGES}
            current={locale}
          />
        </div>
      </div>

      <div className="container">
        <div className="row align-items-center justify-content-between">
          <div className="header-BG" />

          {/* Mobile toggle */}
          <div className="col-sm-4 w-auto d-lg-none d-flex position-relative">
            <button
              type="button"
              className="menu-toggle"
              onClick={() => setIsMobileOpen(true)}
              aria-label="Open menu"
            >
              <img src={MenuToggleIcon} alt="" width="20px" />
            </button>
          </div>

          {/* Desktop left */}
          <nav className="header-menu col-4 d-none d-lg-block">
            <MenuList items={LEFT_ITEMS} onItemClick={handleGoTo} />
          </nav>

          {/* Logo (sempre) */}
          <div className="col-sm-4 w-auto text-center">
            <img className="header-logo" alt="Header logo" src={headerLogo} />
          </div>

          {/* Desktop right */}
          <nav className="header-menu col-4 d-none d-lg-block">
            <MenuList items={RIGHT_ITEMS} onItemClick={handleGoTo} />
          </nav>

          <div className="col-sm-4 w-auto d-lg-none d-flex align-items-center justify-content-end">
            <LanguageSwitcher
              variant="mobile"
              languages={LANGUAGES}
              current={locale}
              showLabelsOnMobile={true}
            />
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <MobileDrawer open={isMobileOpen} onClose={() => setIsMobileOpen(false)}>
        <MenuList items={MENU_ITEMS} onItemClick={handleGoTo} direction="col" />
      </MobileDrawer>
    </header>
  );
};
